import { useLocation } from "react-router";
import { Navbar } from "./Navbar";
import { Sidebar } from "./Sidebar";
import { Footer } from "./Footer";
import { OfflineBanner } from "./OfflineBanner";
import { AnimatedOutlet } from "./AnimatedOutlet";
import { useAuth } from "@/lib/auth";
import { useGoogleOneTap } from "@/hooks/useGoogleOneTap";
import { useGoogleSilentAuth } from "@/hooks/useGoogleSilentAuth";
import { useTokenRotation } from "@/hooks/useTokenRotation";
import { useNotificationNavigation } from "@/hooks/useSocket";
import { TrustDevicePrompt } from "@/components/TrustDevicePrompt";
import { ScrollToTop } from "./ScrollToTop";
import { ScrollRestoration } from "./ScrollRestoration";

export function Layout() {
  const { isAuthenticated } = useAuth();

  // Silent re-auth for Google users — proactively refreshes sessions via FedCM
  useGoogleSilentAuth({ enabled: isAuthenticated });

  // Proactive refresh token rotation — reduces stolen token window
  useTokenRotation({ enabled: isAuthenticated });

  // Navigate in-app when SW receives a notification click while the app is open
  useNotificationNavigation();

  return (
    <div className="min-h-screen bg-background">
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>
      <OfflineBanner />
      <Navbar />
      <TrustDevicePrompt />
      <div className="flex h-[calc(100vh-4rem)]">
        {isAuthenticated && <Sidebar />}
        <main id="main-content" className="flex-1 overflow-y-auto flex flex-col" tabIndex={-1}>
          <div className="flex-1">
            <AnimatedOutlet />
          </div>
          {isAuthenticated && <Footer compact />}
        </main>
      </div>
      <ScrollToTop />
      <ScrollRestoration />
    </div>
  );
}

export function PublicLayout() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  // Show One Tap on public pages (landing, pricing, etc.) for unauthenticated users
  // Login page handles its own One Tap instance, so skip it here
  const isLoginPage = location.pathname === "/login";
  useGoogleOneTap({
    enabled: !isAuthenticated && !isLoginPage,
    autoSelect: true,
    context: "signin",
  });

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>
      <OfflineBanner />
      <Navbar />
      <main id="main-content" className="flex-1" tabIndex={-1}>
        <AnimatedOutlet />
      </main>
      <Footer />
      <ScrollToTop />
      <ScrollRestoration />
    </div>
  );
}
